import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Check, Copy, ChevronRight, PackageCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, discount, inr, PLACEHOLDER } from "@/lib/api";
import { useCart, usePageMeta, useSite } from "@/lib/store";
import { WRAP } from "@/components/store/Chrome";
import { ProductGrid, SectionHeading } from "@/components/store/ProductCard";
import { Paragraphs, TracklistSection, splitContent } from "@/components/store/Tracklist";

const fill = (tpl, p) =>
  (tpl || "")
    .replaceAll("{artist}", p?.artist || "").replaceAll("{title}", p?.title || "")
    .replaceAll("{edition_sep}", p?.edition ? " · " : "").replaceAll("{edition}", p?.edition || "")
    .replaceAll("{format}", p?.format || "LP").replaceAll("{sku}", p?.sku || "")
    .replaceAll("{genre}", p?.genres?.[0] || "").replaceAll("{label}", p?.label || "")
    .replaceAll("{price}", p ? inr(p.price) : "");

function Row({ k, children, testid }) {
  return (
    <div className="flex justify-between gap-6 py-3 border-b border-[#e3e3e3] text-[13px]" data-testid={testid}>
      <span className="label-caps text-[10px] text-[#888] pt-0.5">{k}</span><span className="font-arial text-right">{children}</span>
    </div>
  );
}

const artistLink = (a) => `/shop?artist=${encodeURIComponent(a)}`;
const genreLink = (g) => `/shop?genre=${encodeURIComponent(g)}`;
const Crumb = ({ to, children }) => <><Link to={to} className="hover:text-red">{children}</Link><ChevronRight className="w-3 h-3 inline mx-1.5 -mt-px" /></>;
const fmtLabel = (f) => (/lp$/i.test(f || "LP") ? `${f || "LP"} Vinyl` : f);

export default function Product() {
  const { id } = useParams();
  const { site } = useSite();
  const cart = useCart();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [qty, setQty] = useState(1);
  const p = data?.product;
  const pg = site?.pages?.product;
  const gearMeta = p?.kind === "gear";
  usePageMeta(p && (p.meta_title || (gearMeta ? `${p.title} | Buy online in India | Vinyl Record Vault` : fill(pg?.title, p))),
    p && (p.meta_description || (gearMeta ? `Buy the ${p.title}${p.artist ? ` by ${p.artist}` : ""} online in India for ${inr(p.price)}. ${p.gear_category || "Turntables & accessories"} from Vinyl Record Vault, delivered by BlueDart.` : fill(pg?.description, p))),
    p && (p.meta_keywords || (gearMeta ? `${p.title}, ${p.gear_category || ""}, ${p.artist || ""}, turntables India, vinyl accessories` : fill(pg?.keywords, p))), p?.image);

  useEffect(() => {
    setData(null);
    setQty(1);
    api.get(`/products/${id}`).then((r) => setData(r.data)).catch(() => setErr(true));
    window.scrollTo(0, 0);
  }, [id]);

  if (err) return <div className={`${WRAP} py-32 text-center`}>Record not found. <Link to="/shop" className="text-red underline">Browse the vault</Link></div>;
  if (!p) return <div className={`${WRAP} py-16`}><div className="h-[500px] animate-pulse bg-white/60" /></div>;
  const d = discount(p);
  const soldOut = !p.stock;
  const inCart = cart.items.find((i) => i.product_id === p.id)?.qty || 0;
  const add = () => {
    if (inCart + qty > p.stock) return toast.error("Sorry, we can't add more of this item to your basket");
    cart.add(p, qty);
    toast.success(`${p.artist} – ${p.title} added to your basket`);
  };

  const content = p.kind === "gear" ? { list: null, count: 0, plainDesc: p.description || "", plainTracks: "" } : splitContent(p);
  const gear = p.kind === "gear";
  const gearLink = (c) => `/turntables-accessories?cat=${encodeURIComponent(c)}`;
  const moods = (site?.mood_groups || []).flatMap((g) => g.moods).filter((m) => p.moods?.includes(m.id));
  const copySku = () => {
    navigator.clipboard?.writeText(p.sku).then(() => toast.success("Catalogue number copied"), () => toast.error("Could not copy"));
  };
  return (
    <div className={`${WRAP} pt-8`}>
      <p className="label-caps text-[10px] text-[#888] mb-6 font-arial flex flex-wrap items-center" data-testid="product-breadcrumb">
        {gear ? <Crumb to="/turntables-accessories">Turntables & Accessories</Crumb> : <Crumb to="/shop">All records</Crumb>}
        {gear && p.gear_category && <Crumb to={gearLink(p.gear_category)}>{p.gear_category}</Crumb>}
        {!gear && p.genres?.[0] && <Crumb to={genreLink(p.genres[0])}>{p.genres[0]}</Crumb>}
        {!gear && p.artist && <Crumb to={artistLink(p.artist)}>{p.artist}</Crumb>}
        <span className="text-[#333]">{p.title}</span>
      </p>
      <div className="grid md:grid-cols-[1fr_1fr] gap-10 lg:gap-20">
        <div className="relative group self-start md:mr-[10%] max-md:overflow-x-clip max-md:pr-[10%]" data-testid="product-image-wrap">
          {!gear && <span className="vinyl-peek vinyl-peek-lg" aria-hidden="true" />}
          <div className="relative z-10 bg-white aspect-square border border-[#e3e3e3]">
            <img src={p.image || PLACEHOLDER} alt={`${p.artist} – ${p.title}`} className={`w-full h-full ${gear ? "object-contain" : "object-cover"}`} onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} data-testid="product-image" />
            {d > 0 && !soldOut && <span className="absolute top-4 left-4 bg-red text-white text-xs font-bold font-arial px-2 py-1">-{d}%</span>}
          </div>
        </div>
        <div>
          {p.artist && (gear
            ? <span className="font-arial font-bold uppercase tracking-wide text-sm text-red" data-testid="product-artist">{p.artist}</span>
            : <Link to={artistLink(p.artist)} className="font-arial font-bold uppercase tracking-wide text-sm text-red hover:underline" data-testid="product-artist">{p.artist}</Link>)}
          <h1 className="font-head font-extrabold text-3xl sm:text-[40px] tracking-tight mt-1.5 leading-tight" data-testid="product-title">{p.title}</h1>
          <div className="flex flex-wrap gap-2 mt-4">
            <span className="bg-black text-white text-[10px] font-bold font-arial uppercase px-2.5 py-1.5" data-testid="product-format-chip">{gear ? p.gear_category : fmtLabel(p.format)}</span>
            {p.edition && <span className="border border-black text-[10px] font-bold font-arial uppercase px-2.5 py-1.5" data-testid="product-edition">{p.edition}</span>}
          </div>
          <div className="border-t border-black mt-6 pt-6">
            <div className="flex items-baseline gap-3 font-arial">
              <span className="text-red font-bold text-3xl" data-testid="product-price">{inr(p.price)}</span>
              {p.regular_price && <span className="line-through text-[#999]" data-testid="product-old-price">{inr(p.regular_price)}</span>}
            </div>
            {!gear && <p className="label-caps text-[10px] text-[#888] mt-1.5" data-testid="product-format-label">{p.format || "LP"}</p>}
            <p className={`mt-4 text-[13px] font-arial font-bold flex items-center gap-1.5 ${soldOut ? "text-[#888]" : "text-[#1f7a3a]"}`} data-testid="product-stock">
              <PackageCheck className="w-4 h-4" /> {soldOut ? "Sold out" : "In stock"}
            </p>
            {site?.delivery && (
              <p className={`mt-2 text-[13px] font-arial flex items-center gap-1.5 ${p.express_delivery ? "text-red font-bold" : "text-[#444]"}`} data-testid="product-delivery">
                <Truck className="w-4 h-4" /> {p.express_delivery ? site.delivery.express_text : site.delivery.default_text}
              </p>
            )}
          </div>
          {!soldOut && (
            <div className="mt-5 flex gap-3">
              <div className="flex items-center border border-black bg-white">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-12 grid place-items-center" data-testid="product-qty-minus"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-arial" data-testid="product-qty">{qty}</span>
                <button onClick={() => setQty(Math.min(p.stock, qty + 1))} className="w-10 h-12 grid place-items-center" data-testid="product-qty-plus"><Plus className="w-4 h-4" /></button>
              </div>
              <button onClick={add} className="flex-1 bg-red text-white font-bold text-xs tracking-[0.15em] flex items-center justify-center gap-2 hover:bg-black transition-colors" data-testid="product-add-to-basket">
                <ShoppingBag className="w-4 h-4" /> ADD TO BASKET
              </button>
            </div>
          )}
          {inCart > 0 && <Link to="/basket" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#1f7a3a]" data-testid="product-in-basket"><Check className="w-4 h-4" /> {inCart} in your basket – view basket</Link>}
          <div className="mt-6 border-t border-black" data-testid="product-details">
            <Row k={gear ? "Product code" : "Catalogue no."} testid="product-sku">
              <span className="inline-flex items-center gap-2 font-bold">{p.sku}
                <button onClick={copySku} className="text-[#777] hover:text-red" aria-label="Copy catalogue number" data-testid="product-sku-copy"><Copy className="w-3.5 h-3.5" /></button>
              </span>
            </Row>
            {gear && p.artist && <Row k="Brand" testid="product-detail-brand">{p.artist}</Row>}
            {gear && p.gear_category && <Row k="Category" testid="product-detail-category"><Link to={gearLink(p.gear_category)} className="hover:text-red hover:underline underline-offset-2">{p.gear_category}</Link></Row>}
            {!gear && p.artist && <Row k="Artist" testid="product-detail-artist"><Link to={artistLink(p.artist)} className="hover:text-red underline-offset-2 hover:underline">{p.artist}</Link></Row>}
            {!gear && <Row k="Format" testid="product-detail-format">{p.format || "LP"}</Row>}
            {p.edition && <Row k="Edition / colour" testid="product-detail-edition">{p.edition}</Row>}
            {content.count > 0 && <Row k="Tracks" testid="product-detail-tracks">{content.count}</Row>}
            {p.genres?.length > 0 && (
              <Row k="Genre" testid="product-detail-genre">
                {p.genres.map((g, i) => <span key={g}>{i > 0 && ", "}<Link to={genreLink(g)} className="hover:text-red hover:underline underline-offset-2">{g}</Link></span>)}
              </Row>
            )}
            {p.label && <Row k="Label">{p.label}</Row>}
            {p.release_year && <Row k="Released">{p.release_year}</Row>}
          </div>
          {moods.length > 0 && (
            <div className="mt-5" data-testid="product-moods">
              <p className="label-caps text-[10px] text-[#888] mb-2">Perfect for</p>
              <div className="flex flex-wrap gap-2">{moods.map((m) => <Link key={m.id} to={`/shop?mood=${m.id}`} className="bg-white border border-[#ccc] px-2.5 py-1.5 text-xs hover:border-black transition-colors">{m.name}</Link>)}</div>
            </div>
          )}
          {content.plainDesc.trim() && <div className="mt-8"><Paragraphs text={content.plainDesc} testid="product-description" /></div>}
        </div>
      </div>
      {content.list && <TracklistSection list={content.list} count={content.count} sku={p.sku} />}
      {content.plainTracks && (
        <section className="mt-20"><SectionHeading title="Tracklist" /><Paragraphs text={content.plainTracks} testid="product-tracklist-plain" /></section>
      )}
      {!gear && data.by_artist?.length > 0 && (
        <section className="mt-24">
          <SectionHeading label="From the same artist" title="More by this artist" to={artistLink(p.artist)} id="product-more-by-artist" />
          <ProductGrid products={data.by_artist} testid="product-artist-grid" />
        </section>
      )}
      {data.related.length > 0 && (
        <section className="mt-24">
          <SectionHeading label={gear ? "More gear" : "More from the crates"} title="You might also like" />
          <ProductGrid products={data.related} testid="product-related-grid" />
        </section>
      )}
    </div>
  );
}
